import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Hit, Client, Contact, PressList } from '@pf/shared';
import {
  useHits,
  useCreateHit,
  useUpdateHit,
  useDeleteHit,
  usePrefillHit,
  type CreateHitBody,
} from '../hooks/useHits';
import { useClients } from '../hooks/useClients';
import { useContacts } from '../hooks/useContacts';
import { usePressLists } from '../hooks/usePressLists';

// ── Helpers ───────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

// ── Contact autocomplete ──────────────────────────────────────────────────────

interface ContactPickerProps {
  selectedId: string | null;
  selectedName: string;
  onSelect: (contact: Contact | null) => void;
}

function ContactPicker({ selectedId, selectedName, onSelect }: ContactPickerProps) {
  const [query, setQuery] = useState(selectedName);
  const [open, setOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useContacts({
    search: debouncedQuery || undefined,
    limit: 8,
  });
  const suggestions = (data?.data ?? []) as Contact[];

  useEffect(() => {
    setQuery(selectedName);
  }, [selectedName]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onSelect(null);
  }

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => { if (query) setOpen(true); }}
        placeholder="Search contacts..."
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {selectedId && (
        <button
          type="button"
          onClick={() => { onSelect(null); setQuery(''); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          title="Clear reporter"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {open && suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((c) => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              onClick={() => {
                onSelect(c);
                setQuery(`${c.firstName} ${c.lastName}`);
                setOpen(false);
              }}
            >
              <span className="font-medium">{c.firstName} {c.lastName}</span>
              {(c.title || c.company) && (
                <span className="text-gray-400 ml-2">{[c.title, c.company].filter(Boolean).join(' · ')}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Hit form modal ────────────────────────────────────────────────────────────

interface HitModalProps {
  hit?: Hit | null;
  defaultClientId?: string | null;
  clients: Client[];
  pressLists: PressList[];
  onClose: () => void;
  onSuccess: () => void;
}

function HitModal({ hit, defaultClientId, clients, pressLists, onClose, onSuccess }: HitModalProps) {
  const createHit = useCreateHit();
  const updateHit = useUpdateHit();
  const prefill = usePrefillHit();

  const [url, setUrl] = useState(hit?.url ?? '');
  const [title, setTitle] = useState(hit?.title ?? '');
  const [publicationName, setPublicationName] = useState(hit?.publicationName ?? '');
  const [publicationUrl, setPublicationUrl] = useState(hit?.publicationUrl ?? '');
  const [clientId, setClientId] = useState<string>(hit?.clientId ?? defaultClientId ?? '');
  const [pressListId, setPressListId] = useState<string>(hit?.pressListId ?? '');
  const [selectedContactId, setSelectedContactId] = useState<string | null>(hit?.contactId ?? null);
  const [selectedContactName, setSelectedContactName] = useState('');
  const [publishedAt, setPublishedAt] = useState(toDateInputValue(hit?.publishedAt));
  const [notes, setNotes] = useState(hit?.notes ?? '');
  const [error, setError] = useState('');
  const [prefillLoading, setPrefillLoading] = useState(false);

  const isEditing = !!hit;

  async function handlePrefill() {
    if (!url.trim()) return;
    setPrefillLoading(true);
    setError('');
    try {
      const result = await prefill.mutateAsync(url.trim());
      if (result.title && !title) setTitle(result.title);
      if (result.publicationName && !publicationName) setPublicationName(result.publicationName);
    } catch {
      setError('Could not fetch page metadata. Fill in the fields manually.');
    } finally {
      setPrefillLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const body: CreateHitBody = {
      url: url.trim(),
      title: title.trim(),
      publicationName: publicationName.trim(),
      publicationUrl: publicationUrl.trim() || null,
      clientId: clientId || null,
      pressListId: pressListId || null,
      contactId: selectedContactId,
      publishedAt: publishedAt || null,
      notes: notes.trim() || null,
    };
    try {
      if (isEditing) {
        await updateHit.mutateAsync({ id: hit.id, body });
      } else {
        await createHit.mutateAsync(body);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hit');
    }
  }

  const isPending = createHit.isPending || updateHit.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{isEditing ? 'Edit Coverage' : 'Log Coverage'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form id="hit-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* URL + Prefill */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Article URL *</label>
            <div className="flex gap-2">
              <input
                required
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handlePrefill}
                disabled={!url.trim() || prefillLoading}
                className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 flex-shrink-0"
              >
                {prefillLoading ? 'Fetching...' : 'Prefill'}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-400">Paste the article URL and click Prefill to auto-extract title and publication.</p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Article title"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Publication name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Publication *</label>
            <input
              required
              value={publicationName}
              onChange={(e) => setPublicationName(e.target.value)}
              placeholder="e.g. TechCrunch"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Publication URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Publication URL</label>
            <input
              type="url"
              value={publicationUrl}
              onChange={(e) => setPublicationUrl(e.target.value)}
              placeholder="https://techcrunch.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Reporter (contact search) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reporter
              <span className="ml-1 font-normal text-gray-400 text-xs">(link to a contact)</span>
            </label>
            <ContactPicker
              selectedId={selectedContactId}
              selectedName={selectedContactName}
              onSelect={(c) => {
                setSelectedContactId(c?.id ?? null);
                setSelectedContactName(c ? `${c.firstName} ${c.lastName}` : '');
              }}
            />
          </div>

          {/* Client */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">No client</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Press list */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Press List</label>
            <select
              value={pressListId}
              onChange={(e) => setPressListId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">No press list</option>
              {pressLists.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Published date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Published Date</label>
            <input
              type="date"
              value={publishedAt}
              onChange={(e) => setPublishedAt(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="hit-form"
            disabled={isPending}
            className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
          >
            {isPending ? 'Saving...' : isEditing ? 'Save changes' : 'Log hit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Row actions ───────────────────────────────────────────────────────────────

interface RowActionsProps {
  onEdit: () => void;
  onDelete: () => void;
  busy: boolean;
}

function RowActions({ onEdit, onDelete, busy }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="p-1 text-gray-400 hover:text-gray-600 rounded disabled:opacity-50"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          <button
            onClick={() => { onEdit(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
          >
            Edit
          </button>
          <button
            onClick={() => { onDelete(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function HitsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [clientIdFilter, setClientIdFilter] = useState(searchParams.get('clientId') ?? '');
  const [pressListIdFilter, setPressListIdFilter] = useState(searchParams.get('pressListId') ?? '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const [showCreate, setShowCreate] = useState(false);
  const [editingHit, setEditingHit] = useState<Hit | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const deleteHit = useDeleteHit();

  const { data: hitsData, isLoading, isError } = useHits({
    page,
    limit: 50,
    clientId: clientIdFilter || undefined,
    pressListId: pressListIdFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const { data: clientsData } = useClients({ limit: 100 });
  const { data: pressListsData } = usePressLists();

  const hits = hitsData?.hits ?? [];
  const total = hitsData?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  const clients = useMemo(
    () => (clientsData as unknown as { clients: Client[] } | undefined)?.clients ?? [],
    [clientsData],
  );
  const clientsById = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients],
  );

  const pressLists = useMemo(
    () => (pressListsData as unknown as { pressLists: PressList[] } | undefined)?.pressLists ?? [],
    [pressListsData],
  );

  function handleFilterChange() {
    setPage(1);
    const params: Record<string, string> = {};
    if (clientIdFilter) params.clientId = clientIdFilter;
    if (pressListIdFilter) params.pressListId = pressListIdFilter;
    setSearchParams(params, { replace: true });
  }

  useEffect(() => {
    handleFilterChange();
  }, [clientIdFilter, pressListIdFilter, dateFrom, dateTo]);

  async function handleDelete(id: string) {
    if (!confirm('Delete this hit? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteHit.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  }

  function handleModalSuccess() {
    setShowCreate(false);
    setEditingHit(null);
  }

  return (
    <div className="p-6">
      {(showCreate || editingHit) && (
        <HitModal
          hit={editingHit}
          defaultClientId={clientIdFilter || null}
          clients={clients}
          pressLists={pressLists}
          onClose={() => { setShowCreate(false); setEditingHit(null); }}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Coverage</h1>
          {total > 0 && (
            <p className="mt-1 text-sm text-gray-500">{total} hit{total !== 1 ? 's' : ''}</p>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
        >
          Log Coverage
        </button>
      </div>

      {/* Filters */}
      <div className="mb-5 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
          <select
            value={clientIdFilter}
            onChange={(e) => setClientIdFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Press List</label>
          <select
            value={pressListIdFilter}
            onChange={(e) => setPressListIdFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All lists</option>
            {pressLists.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {(clientIdFilter || pressListIdFilter || dateFrom || dateTo) && (
          <button
            onClick={() => {
              setClientIdFilter('');
              setPressListIdFilter('');
              setDateFrom('');
              setDateTo('');
              setSearchParams({}, { replace: true });
            }}
            className="text-sm text-gray-500 hover:text-gray-700 self-end pb-2"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Loading / error states */}
      {isLoading && (
        <div className="text-center py-10 text-sm text-gray-500">Loading...</div>
      )}
      {isError && (
        <div className="text-center py-10 text-sm text-red-600">Failed to load coverage.</div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && hits.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-500">
            {clientIdFilter || pressListIdFilter || dateFrom || dateTo
              ? 'No hits match the current filters.'
              : 'No coverage logged yet. Click "Log Coverage" to add your first hit.'}
          </p>
        </div>
      )}

      {/* Hits table */}
      {!isLoading && hits.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Publication</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {hits.map((hit) => (
                <tr key={hit.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 max-w-xs">
                    <a
                      href={hit.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:underline line-clamp-2"
                    >
                      {hit.title}
                    </a>
                    {hit.notes && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{hit.notes}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {hit.publicationUrl ? (
                      <a
                        href={hit.publicationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-gray-700 hover:text-blue-600"
                      >
                        {hit.publicationName}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-700">{hit.publicationName}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {hit.contactId ? (
                      <Link
                        to={`/contacts/${hit.contactId}`}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Reporter
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {hit.clientId && clientsById[hit.clientId] ? (
                      <Link
                        to={`/clients/${hit.clientId}`}
                        className="text-sm text-gray-700 hover:text-blue-600"
                      >
                        {clientsById[hit.clientId].name}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">{formatDate(hit.publishedAt)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <RowActions
                      onEdit={() => setEditingHit(hit)}
                      onDelete={() => handleDelete(hit.id)}
                      busy={deletingId === hit.id}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
